/**
 * Jeton d'invitation à voter (`?invite=<token>`).
 *
 * Le lien personnel reçu par email porte le jeton en query string. On le
 * déplace immédiatement en sessionStorage et on le RETIRE de l'URL : laissé en
 * place, il partirait dans l'en-tête `Referer` de toute ressource externe, dans
 * les outils d'analytics, dans les captures d'écran et dans l'historique
 * partagé du navigateur. Un jeton de vote est un secret porteur — il ne doit
 * pas rester affiché.
 *
 * Portée sessionStorage (et non localStorage) : le jeton ne survit pas à la
 * fermeture de l'onglet, ce qui limite la fenêtre d'exposition sur un poste
 * partagé. L'invité peut toujours rouvrir son lien.
 */

const KEY_PREFIX = "dd_invite_";

function storageKey(pollKey: string): string {
  return `${KEY_PREFIX}${pollKey}`;
}

function safeSession(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    // Safari en navigation privée, cookies bloqués…
    return null;
  }
}

/**
 * Lit `?invite=` s'il est présent, le range, et nettoie l'URL.
 * Retourne le jeton courant pour ce sondage (fraîchement capté ou mémorisé).
 */
export function captureInviteToken(pollKey: string): string | null {
  if (typeof window === "undefined") return null;

  let fromUrl: string | null = null;
  try {
    const params = new URLSearchParams(window.location.search);
    fromUrl = params.get("invite");
    if (fromUrl) {
      params.delete("invite");
      const qs = params.toString();
      const cleaned =
        window.location.pathname +
        (qs ? `?${qs}` : "") +
        window.location.hash;
      window.history.replaceState(null, "", cleaned);
    }
  } catch {
    // URL illisible : on retombe sur le jeton éventuellement mémorisé.
  }

  const store = safeSession();
  if (fromUrl) {
    const token = fromUrl.trim();
    if (token) {
      try {
        store?.setItem(storageKey(pollKey), token);
      } catch {
        // Quota / mode privé : le jeton reste utilisable pour ce rendu.
      }
      return token;
    }
  }

  try {
    return store?.getItem(storageKey(pollKey)) ?? null;
  } catch {
    return null;
  }
}

/** Jeton mémorisé pour ce sondage, sans toucher à l'URL. */
export function getInviteToken(pollKey: string): string | null {
  try {
    return safeSession()?.getItem(storageKey(pollKey)) ?? null;
  } catch {
    return null;
  }
}

/** Oublie le jeton (invitation révoquée, épuisée, ou changement d'invité). */
export function clearInviteToken(pollKey: string): void {
  try {
    safeSession()?.removeItem(storageKey(pollKey));
  } catch {
    // rien à faire
  }
}

/**
 * Marque un sondage comme « sur invitation », posé par la page de vote quand
 * elle lit les réglages.
 *
 * Sert à n'envoyer `p_verified_email` à la RPC que dans ce cas. L'envoyer à
 * chaque vote d'utilisateur connecté ajouterait un argument nommé que la base
 * ne connaît pas tant que la migration n'est pas appliquée — PostgREST
 * résolvant par l'ensemble exact des arguments, tous les votes casseraient.
 *
 * Ce marqueur est purement indicatif et ne donne aucun droit : c'est le
 * serveur qui décide. Un client qui mentirait en le posant à tort ferait
 * seulement transiter un email vérifié sur un sondage public, où la RPC
 * l'ignore ; et l'omettre à tort ne fait que refuser l'accès (fail closed).
 */
export function markInviteOnly(pollKey: string, value: boolean): void {
  try {
    const store = safeSession();
    if (!store) return;
    if (value) store.setItem(`${KEY_PREFIX}only_${pollKey}`, "1");
    else store.removeItem(`${KEY_PREFIX}only_${pollKey}`);
  } catch {
    // rien à faire
  }
}

/** Le sondage est-il connu comme « sur invitation » ? */
export function isInviteOnly(pollKey: string): boolean {
  try {
    return safeSession()?.getItem(`${KEY_PREFIX}only_${pollKey}`) === "1";
  } catch {
    return false;
  }
}
